// src/pages/FaturaExcelYukle.js
import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { db, auth } from "../firebaseConfig";
import {
  collection,
  addDoc,
  getDocs,
  serverTimestamp,
  Timestamp,
  query,
  where,
} from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";

const FaturaExcelYukle = () => {
  const { userPermissions } = useAuth();
  const [dosya, setDosya] = useState(null);
  const [firmalar, setFirmalar] = useState([]);
  const [plakalar, setPlakalar] = useState([]);
  const [progress, setProgress] = useState(0);
  const [hataDosyasiAktif, setHataDosyasiAktif] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const [firmaSnap, plakaSnap] = await Promise.all([
        getDocs(collection(db, "firms")),
        getDocs(collection(db, "plates")),
      ]);

      setFirmalar(
        firmaSnap.docs.map((d) => ({
          id: d.id,
          adi: d.data().firmaAdi,
          kdvOrani: d.data().kdvOrani ?? 18,
        }))
      );

      setPlakalar(
        plakaSnap.docs.map((d) =>
          (d.data().plate || "").toString().trim().toUpperCase()
        )
      );
    };

    fetchData();
  }, []);

  if (!userPermissions?.yonetici) {
    return (
      <div style={{ padding: 20 }}>
        <h2 style={{ fontSize: "1.2rem", fontWeight: "bold", color: "red" }}>
          Erişiminiz yok.
        </h2>
      </div>
    );
  }

  const excelTarihiniDonustur = (deger) => {
    if (typeof deger === "number") {
      return new Date((deger - 25569) * 86400 * 1000);
    }
    const parsed = new Date(deger);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const handleDosyaSec = (e) => setDosya(e.target.files[0]);

  const handleYukle = async () => {
    if (!dosya) return alert("Lütfen bir Excel dosyası seçin.");

    const reader = new FileReader();
    reader.onload = async (e) => {
      setProgress(0);
      const workbook = XLSX.read(e.target.result, { type: "binary" });
      const ws = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws);

      const gruplar = {};
      const hataliSatirlar = [];

      // Satırları grupla (Fatura No + Firma)
      for (let row of json) {
        const faturaNo = row["Fatura No"]?.toString().trim();
        const firma = row["Firma"]?.toString().trim();
        if (!faturaNo || !firma) {
          hataliSatirlar.push({
            ...row,
            "Hata Nedeni": "Fatura No veya Firma eksik",
          });
          continue;
        }
        const grupKey = `${faturaNo}__${firma}`;
        if (!gruplar[grupKey]) gruplar[grupKey] = [];
        gruplar[grupKey].push(row);
      }

      const grupAnahtarlari = Object.keys(gruplar);
      const chunkSize = 50;
      const totalGroups = grupAnahtarlari.length;
      let processedGroups = 0;
      let basarili = 0;

      for (let i = 0; i < totalGroups; i += chunkSize) {
        const parcaliGruplar = grupAnahtarlari.slice(i, i + chunkSize);

        for (const grupKey of parcaliGruplar) {
          const rows = gruplar[grupKey];
          const kontrolSetFirma = new Set();
          const kontrolSetTarih = new Set();
          let firmaId = "";
          let firmaAdi = "";
          let tarihJS;
          let satirlar = [];
          let hataliMi = false;

          for (let row of rows) {
            const firma = row["Firma"]?.toString().trim();
            tarihJS = excelTarihiniDonustur(row["Tarih"]);
            const plaka = row["Plaka"]?.toString().trim().toUpperCase();
            const urunAdi = row["Ürün Adı"]?.toString().trim();

            // Fiyat, Adet, İskonto virgül-nokta düzeltme
            const fiyatStr = row["Fiyat"]?.toString().trim().replace(",", ".");
            const adetStr = row["Adet"]?.toString().trim().replace(",", ".");
            const iskontoStr =
              row["İskonto"]?.toString().trim().replace(",", ".") ?? "0";

            const fiyat = parseFloat(fiyatStr);
            const adet = parseFloat(adetStr);
            const kdvRaw = (row["KDV"] ?? "").toString().trim().replace(",", ".");
            const kdv = parseFloat(kdvRaw);
            const aciklama = row["Açıklama"] ?? "";
            const iskonto = parseFloat(iskontoStr);

            const firmaObj = firmalar.find((f) => f.adi === firma);
            if (!firma || !firmaObj) {
              hataliSatirlar.push({ ...row, "Hata Nedeni": "Firma bulunamadı" });
              hataliMi = true;
              break;
            }
            if (!tarihJS) {
              hataliSatirlar.push({ ...row, "Hata Nedeni": "Tarih hatalı" });
              hataliMi = true;
              break;
            }
            if (!plakalar.includes(plaka)) {
              hataliSatirlar.push({ ...row, "Hata Nedeni": "Plaka geçersiz" });
              hataliMi = true;
              break;
            }
            if (
              !urunAdi ||
              isNaN(fiyat) ||
              fiyat < 0 || // 0 kabul
              isNaN(adet) ||
              adet <= 0 || // adet 0 olmaz
              isNaN(kdv) ||
              kdv < 0 || // 0 kabul
              kdv >= 100
            ) {
              hataliSatirlar.push({
                ...row,
                "Hata Nedeni": "Geçersiz ürün/fiyat/adet/kdv",
              });
              hataliMi = true;
              break;
            }

            kontrolSetFirma.add(firma);
            kontrolSetTarih.add(tarihJS.toISOString());
            firmaId = firmaObj.id;
            firmaAdi = firma;

            satirlar.push({
              plaka,
              urunAdi,
              fiyat: Number(fiyat).toFixed(2),
              adet: Number(adet).toFixed(2),
              iskonto: Number(iskonto || 0).toFixed(2),
              kdv,
              aciklama,
            });
          }

          // Aynı faturaNo için farklı firma/tarih kontrolü
          if (kontrolSetFirma.size > 1) {
            rows.forEach((r) =>
              hataliSatirlar.push({
                ...r,
                "Hata Nedeni": "Farklı firmalar aynı faturaNo ile girilmiş",
              })
            );
            processedGroups++;
            setProgress(Math.min(100, Math.round((processedGroups / totalGroups) * 100)));
            continue;
          }
          if (kontrolSetTarih.size > 1) {
            rows.forEach((r) =>
              hataliSatirlar.push({
                ...r,
                "Hata Nedeni": "Farklı tarihler aynı faturaNo ile girilmiş",
              })
            );
            processedGroups++;
            setProgress(Math.min(100, Math.round((processedGroups / totalGroups) * 100)));
            continue;
          }
          if (hataliMi) {
            processedGroups++;
            setProgress(Math.min(100, Math.round((processedGroups / totalGroups) * 100)));
            continue;
          }

          const faturaNo = rows[0]["Fatura No"]?.toString().trim();

          // YALNIZCA AYNI FİRMA İÇİN DUPLICATE KONTROLÜ (farklı firmalar aynı numarayı kullanabilir)
          const snapshot = await getDocs(
            query(
              collection(db, "faturalar"),
              where("faturaNo", "==", faturaNo),
              where("firmaId", "==", firmaId),
              where("deleted", "==", false)
            )
          );

          if (!snapshot.empty) {
            rows.forEach((r) =>
              hataliSatirlar.push({
                ...r,
                "Hata Nedeni": "Aynı firma için aynı fatura zaten kayıtlı",
              })
            );
            processedGroups++;
            setProgress(Math.min(100, Math.round((processedGroups / totalGroups) * 100)));
            continue;
          }

          await addDoc(collection(db, "faturalar"), {
            faturaNo,
            firmaAdi,
            firmaId,
            tarih: Timestamp.fromDate(tarihJS),
            deleted: false,
            createdBy: auth.currentUser?.email || "excel-import",
            createdAt: serverTimestamp(),
            faturaAcik: "",
            satirlar,
          });

          basarili += satirlar.length;
          processedGroups++;
          setProgress(Math.min(100, Math.round((processedGroups / totalGroups) * 100)));
          await new Promise((r) => setTimeout(r, 50)); // UI nefes alsın
        }
      }

      alert(
        `Excel'den fatura yükleme tamamlandı ✅\nToplam başarıyla yüklenen satır: ${basarili}`
      );
      setProgress(0);

      if (hataDosyasiAktif && hataliSatirlar.length > 0) {
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(hataliSatirlar);
        XLSX.utils.book_append_sheet(wb, ws, "Hatalar");
        XLSX.writeFile(wb, "fatura_yukleme_hatalari.xlsx");
      }
    };

    reader.readAsBinaryString(dosya);
  };

  const handleOrnekIndir = () => {
    const workbook = XLSX.utils.book_new();
    const veri = [
      {
        "Fatura No": "INO2025001",
        Firma: "ACME A.Ş.",
        Tarih: new Date().toISOString().split("T")[0],
        Plaka: "06ABC123",
        "Ürün Adı": "Takoz",
        Fiyat: 100,
        Adet: 2,
        KDV: 18,
        İskonto: 5,
        Açıklama: "Numune",
      },
    ];
    const ws = XLSX.utils.json_to_sheet(veri);
    XLSX.utils.book_append_sheet(workbook, ws, "Faturalar");
    XLSX.writeFile(workbook, "ornek_fatura.xlsx");
  };

  return (
    <div style={{ padding: 20, maxWidth: 800, margin: "auto" }}>
      <h2
        style={{
          fontSize: "1.5rem",
          fontWeight: "bold",
          color: "#4f46e5",
          marginBottom: 20,
        }}
      >
        Excel ile Fatura Yükle
      </h2>
      <input
        type="file"
        accept=".xlsx,.xls"
        onChange={handleDosyaSec}
        style={{ marginBottom: 10 }}
      />
      <div style={{ marginBottom: 10 }}>
        <label>
          <input
            type="checkbox"
            checked={hataDosyasiAktif}
            onChange={(e) => setHataDosyasiAktif(e.target.checked)}
          />{" "}
          Hata Dosyası Oluştur
        </label>
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button
          onClick={handleYukle}
          style={{
            backgroundColor: "#16a34a",
            color: "white",
            padding: "8px 16px",
            borderRadius: 6,
            border: "none",
          }}
        >
          Yükle
        </button>
        <button
          onClick={handleOrnekIndir}
          style={{
            backgroundColor: "#2563eb",
            color: "white",
            padding: "8px 16px",
            borderRadius: 6,
            border: "none",
          }}
        >
          Örnek Şablon İndir
        </button>
      </div>
      {progress > 0 && (
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              backgroundColor: "#e5e7eb",
              height: 20,
              borderRadius: 6,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: "100%",
                backgroundColor: "#4f46e5",
                transition: "width 0.2s",
              }}
            />
          </div>
          <p style={{ marginTop: 5 }}>{progress}% tamamlandı...</p>
        </div>
      )}
    </div>
  );
};

export default FaturaExcelYukle;
